import { ChangeDetectionStrategy, Component, DestroyRef, OnInit, computed, effect, inject, input, output, signal } from '@angular/core';
import { takeUntilDestroyed, toSignal } from '@angular/core/rxjs-interop';
import { FormBuilder, FormControl, ReactiveFormsModule } from '@angular/forms';
import { ActivatedRoute, Router } from '@angular/router';

import { TranslatePipe } from '@ngx-translate/core';
import { debounceTime, distinctUntilChanged, map } from 'rxjs';

import { MatFormField, MatLabel, MatPrefix } from '@angular/material/form-field';
import { MatIcon } from '@angular/material/icon';
import { MatInput } from '@angular/material/input';
import { MatTab, MatTabGroup, MatTabLabel } from '@angular/material/tabs';

import { ChatThreadsListComponent } from '../chat-threads-list/chat-threads-list.component';
import { ChatStateService } from '@lib/chat/services/chat-state.service';
import { ChatTabFilter, ChatThread, ChatThreadSearchRequest } from '@lib/chat/models/chat.interface';

import { LoaderComponent } from '@lib/shared-ui/layout/loader/loader.component';

@Component({
  selector: 'chat-tabs',
  standalone: true,
  imports: [MatTabGroup, MatTab, TranslatePipe, ChatThreadsListComponent, MatFormField, MatIcon, MatInput, MatLabel, MatPrefix, ReactiveFormsModule, MatTabLabel, LoaderComponent],
  templateUrl: './chat-tabs.component.html',
  styles: `
    :host {
      @apply h-full;
    }
  `,
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class ChatTabsComponent implements OnInit {
  private readonly fb = inject(FormBuilder);
  private readonly destroyRef = inject(DestroyRef);
  private readonly router = inject(Router);
  private readonly route = inject(ActivatedRoute);
  private readonly chatState = inject(ChatStateService);

  selectedThread = input.required<ChatThread>();
  threads = input.required<ChatThread[]>();
  unreads = input.required<Map<string, number>>();
  ownUserId = input.required<string>();
  isLoading = input<boolean>(true);
  isSearching = input<boolean>(false);

  openThread = output<ChatThread>();
  searchThreads = output<ChatThreadSearchRequest>();
  searchOutput = output<string>();
  pinThread = output<string>();
  unpinThread = output<string>();
  assigneeChanged = output<{ threadId: string; assignee: { id: string; name: string } | null }>();

  protected readonly activeTab = signal<ChatTabFilter>(ChatTabFilter.ALL_CHATS);
  protected readonly systemUsers = computed(() => this.chatState.systemUsers());
  protected readonly searchFc: FormControl<string> = this.fb.control<string>(null);

  protected filteredThreads = computed(() => {
    if (this.chatState.isSearching()) return this.chatState.threads();

    switch (this.activeTab()) {
      case ChatTabFilter.ALL_CHATS:
        return this.chatState.allThreads();
      case ChatTabFilter.MY_CHATS:
        return this.chatState.myThreads();
      case ChatTabFilter.IN_QUEUE:
        return this.chatState.inQueueThreads();
      default:
        return this.chatState.threads();
    }
  });

  myChatsCount = input<number>(0);
  inQueueCount = input<number>(0);

  private readonly currentTabFromRoute = toSignal(
    this.route.queryParams.pipe(
      map(params => {
        const chatTabParam = params['chatTab'];
        switch (chatTabParam) {
          case 'myChats':
            return ChatTabFilter.MY_CHATS;
          case 'awaiting':
            return ChatTabFilter.IN_QUEUE;
          case 'all':
          default:
            return ChatTabFilter.ALL_CHATS;
        }
      })
    ),
    { initialValue: ChatTabFilter.ALL_CHATS }
  );

  protected readonly tabIndex = computed(() => {
    switch (this.currentTabFromRoute()) {
      case ChatTabFilter.IN_QUEUE:
        return 1;
      case ChatTabFilter.MY_CHATS:
        return 2;
      default:
        return 0;
    }
  });

  // Effect to sync route with active tab and load data
  private readonly routeTabEffect = effect(() => {
    const routeTab = this.currentTabFromRoute();

    this.activeTab.set(routeTab);
    this.emitSearchRequest(routeTab);
  });

  ngOnInit() {
    this.initThreadsSearch();
    this.ensureDefaultParams();
  }

  private ensureDefaultParams() {
    const currentParams = this.route.snapshot.queryParams;
    const updatedParams: any = {};

    if (!currentParams['chatTab']) updatedParams.chatTab = 'all';
    if (Object.keys(updatedParams).length > 0) {
      this.router.navigate([], {
        relativeTo: this.route,
        queryParams: updatedParams,
        queryParamsHandling: 'merge',
      });
    }
  }

  private initThreadsSearch() {
    this.searchFc.valueChanges.pipe(debounceTime(400), distinctUntilChanged(), takeUntilDestroyed(this.destroyRef)).subscribe(value => this.searchOutput.emit(value));
  }

  onTabChange(index: number) {
    const tabFilters = [ChatTabFilter.ALL_CHATS, ChatTabFilter.IN_QUEUE, ChatTabFilter.MY_CHATS];
    const selectedFilter = tabFilters[index];
    const queryParam = this.getTabQueryParam(selectedFilter);

    this.router.navigate([], {
      relativeTo: this.route,
      queryParams: { chatTab: queryParam },
      queryParamsHandling: 'merge',
    });
  }

  private getTabQueryParam(filter: ChatTabFilter): string {
    switch (filter) {
      case ChatTabFilter.IN_QUEUE:
        return 'awaiting';
      case ChatTabFilter.MY_CHATS:
        return 'myChats';
      default:
        return 'all';
    }
  }

  onOpenThread(thread: ChatThread) {
    this.openThread.emit(thread);
  }

  onPinThread(threadId: string) {
    this.pinThread.emit(threadId);
  }

  onUnpinThread(threadId: string) {
    this.unpinThread.emit(threadId);
  }

  private emitSearchRequest(filter: ChatTabFilter) {
    const userId = this.ownUserId();
    let searchRequest: ChatThreadSearchRequest;

    switch (filter) {
      case ChatTabFilter.MY_CHATS:
        searchRequest = { assigneeId: userId };
        break;
      case ChatTabFilter.IN_QUEUE:
        searchRequest = { assigneeId: null };
        break;
      default:
        searchRequest = {}; // No filters - get all threads
    }

    this.searchThreads.emit(searchRequest);
  }
}
